import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useComplexDetail } from '../../../hooks/venue/useComplexDetail';
import { apiRequest } from '../../../utils/apiUtils';
import { theme } from '../../../theme';
import {
  ArrowLeft,
  Edit,
  Plus,
  MapPin,
  Phone,
  Mail,
  Globe,
  Trash2,
  Power,
  Grid,
  Car,
  ShieldCheck,
  Home as HomeIcon,
  Coffee,
  ShoppingBag,
  Clock,
  DollarSign,
  ExternalLink,
  Building2,
} from 'lucide-react';

const ComplexDetailPage: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id?: string }>();
  const { complex, loading, error, fetchComplex } = useComplexDetail(id);

  useEffect(() => {
    fetchComplex();
  }, [fetchComplex]);

  const handleToggleActive = async () => {
    if (!complex) return;

    const confirm = window.confirm(
      `¿Estás seguro que querés ${complex.isActive ? 'desactivar' : 'activar'} este complejo?`
    );

    if (!confirm) return;

    try {
      await apiRequest(`/complexes/${complex.id}`, {
        method: 'PUT',
        body: { isActive: !complex.isActive },
      });

      alert('Complejo actualizado');
      fetchComplex();
    } catch (err) {
      alert('Error al actualizar el complejo');
    }
  };

  const loadingStyle: React.CSSProperties = {
    minHeight: '100vh',
    backgroundColor: theme.colors.background,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  };

  const loadingTextStyle: React.CSSProperties = {
    fontSize: '18px',
    fontFamily: theme.fonts.body,
    color: theme.colors.textSecondary,
  };

  const errorContainerStyle: React.CSSProperties = {
    minHeight: '100vh',
    backgroundColor: theme.colors.background,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '20px',
  };

  const errorTextStyle: React.CSSProperties = {
    fontSize: '18px',
    fontFamily: theme.fonts.body,
    color: theme.colors.danger,
  };

  const buttonStyle: React.CSSProperties = {
    backgroundColor: theme.colors.primary,
    border: 'none',
    borderRadius: '10px',
    padding: '12px 24px',
    fontSize: '14px',
    fontFamily: theme.fonts.body,
    color: '#000000',
    fontWeight: '600',
    cursor: 'pointer',
  };

  if (loading) {
    return (
      <div style={loadingStyle}>
        <div style={loadingTextStyle}>Cargando complejo...</div>
      </div>
    );
  }

  if (error || !complex) {
    return (
      <div style={errorContainerStyle}>
        <Building2 size={64} color={theme.colors.textSecondary} />
        <p style={errorTextStyle}>No tenés un complejo creado aún</p>
        <p style={{ fontSize: '14px', color: theme.colors.textSecondary, marginTop: '8px' }}>
          Creá tu complejo deportivo para empezar a gestionar tus canchas
        </p>
        <div style={{ display: 'flex', gap: '12px', marginTop: '24px' }}>
          <button
            onClick={() => navigate('/owner/complex/create')}
            style={{
              ...buttonStyle,
              backgroundColor: theme.colors.primary,
              color: '#000',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}
          >
            <Plus size={18} />
            Crear Mi Complejo
          </button>
          <button
            onClick={() => navigate('/owner/dashboard')}
            style={{
              ...buttonStyle,
              backgroundColor: 'transparent',
              border: `2px solid ${theme.colors.border}`,
              color: theme.colors.text
            }}
          >
            Volver al Dashboard
          </button>
        </div>
      </div>
    );
  }

  const venues = complex.venues || [];

  // Styles
  const containerStyle: React.CSSProperties = {
    minHeight: '100vh',
    backgroundColor: theme.colors.background,
  };

  const headerStyle: React.CSSProperties = {
    backgroundColor: theme.colors.surface,
    borderBottom: `1px solid ${theme.colors.border}`,
    padding: '32px 48px',
  };

  const backButtonStyle: React.CSSProperties = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '8px',
    background: 'none',
    border: 'none',
    color: theme.colors.primary,
    fontSize: '14px',
    fontFamily: theme.fonts.body,
    cursor: 'pointer',
    padding: 0,
    marginBottom: '12px',
  };

  const headerContentStyle: React.CSSProperties = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
  };

  const titleStyle: React.CSSProperties = {
    fontSize: '36px',
    fontFamily: theme.fonts.heading,
    color: theme.colors.text,
    fontWeight: 'normal',
    letterSpacing: '0.5px',
    margin: 0,
    marginBottom: '8px',
  };

  const subtitleStyle: React.CSSProperties = {
    fontSize: '14px',
    fontFamily: theme.fonts.body,
    color: theme.colors.textSecondary,
    margin: 0,
  };

  const actionButtonsStyle: React.CSSProperties = {
    display: 'flex',
    gap: '12px',
  };

  const editButtonStyle: React.CSSProperties = {
    backgroundColor: theme.colors.surface,
    border: `1px solid ${theme.colors.primary}`,
    borderRadius: '10px',
    padding: '10px 20px',
    fontSize: '14px',
    fontFamily: theme.fonts.body,
    color: theme.colors.primary,
    fontWeight: '600',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  };

  const addButtonStyle: React.CSSProperties = {
    backgroundColor: theme.colors.primary,
    border: 'none',
    borderRadius: '10px',
    padding: '10px 20px',
    fontSize: '14px',
    fontFamily: theme.fonts.body,
    color: '#000000',
    fontWeight: '600',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  };

  const toggleButtonStyle: React.CSSProperties = {
    backgroundColor: theme.colors.surface,
    border: `1px solid ${theme.colors.border}`,
    borderRadius: '10px',
    padding: '10px 20px',
    fontSize: '14px',
    fontFamily: theme.fonts.body,
    color: theme.colors.text,
    fontWeight: '600',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  };

  const mainContentStyle: React.CSSProperties = {
    padding: '32px 48px',
    display: 'flex',
    flexDirection: 'column',
    gap: '32px',
  };

  const sectionStyle: React.CSSProperties = {
    backgroundColor: theme.colors.surface,
    border: `1px solid ${theme.colors.border}`,
    borderRadius: '12px',
    padding: '24px',
  };

  const sectionTitleStyle: React.CSSProperties = {
    fontSize: '20px',
    fontFamily: theme.fonts.heading,
    color: theme.colors.text,
    fontWeight: 'normal',
    letterSpacing: '0.5px',
    margin: 0,
    marginBottom: '20px',
  };

  const gridTwoColumnsStyle: React.CSSProperties = {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '32px',
  };

  const infoColumnStyle: React.CSSProperties = {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
  };

  const infoRowStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '12px',
  };

  const infoLabelStyle: React.CSSProperties = {
    fontSize: '13px',
    fontFamily: theme.fonts.body,
    color: theme.colors.textSecondary,
    minWidth: '100px',
  };

  const infoValueStyle: React.CSSProperties = {
    fontSize: '14px',
    fontFamily: theme.fonts.body,
    color: theme.colors.text,
    fontWeight: '600',
  };

  const linkStyle: React.CSSProperties = {
    ...infoValueStyle,
    color: theme.colors.primary,
    textDecoration: 'none',
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
  };

  const amenitiesBadgeStyle: React.CSSProperties = {
    display: 'inline-block',
    backgroundColor: 'rgba(0, 230, 118, 0.1)',
    color: theme.colors.primary,
    padding: '6px 12px',
    borderRadius: '6px',
    fontSize: '12px',
    fontWeight: '600',
    marginRight: '8px',
    marginBottom: '8px',
  };

  const sectionHeaderStyle: React.CSSProperties = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '20px',
  };

  const venueCountStyle: React.CSSProperties = {
    fontSize: '13px',
    fontFamily: theme.fonts.body,
    color: theme.colors.textSecondary,
  };

  const addVenueButtonStyle: React.CSSProperties = {
    backgroundColor: theme.colors.primary,
    border: 'none',
    borderRadius: '8px',
    padding: '10px 20px',
    fontSize: '14px',
    fontFamily: theme.fonts.body,
    color: '#000000',
    fontWeight: '600',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  };

  const emptyStateStyle: React.CSSProperties = {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '60px 20px',
    gap: '16px',
  };

  const emptyTextStyle: React.CSSProperties = {
    fontSize: '16px',
    fontFamily: theme.fonts.body,
    color: theme.colors.textSecondary,
    textAlign: 'center',
  };

  const addFirstVenueButtonStyle: React.CSSProperties = {
    backgroundColor: theme.colors.primary,
    border: 'none',
    borderRadius: '10px',
    padding: '14px 28px',
    fontSize: '14px',
    fontFamily: theme.fonts.body,
    color: '#000000',
    fontWeight: '600',
    cursor: 'pointer',
    marginTop: '12px',
  };

  const venuesGridStyle: React.CSSProperties = {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
    gap: '20px',
  };

  const venueCardStyle: React.CSSProperties = {
    backgroundColor: theme.colors.background,
    border: `1px solid ${theme.colors.border}`,
    borderRadius: '10px',
    overflow: 'hidden',
    transition: 'transform 0.2s, border-color 0.2s',
    cursor: 'pointer',
  };

  const venueImageStyle: React.CSSProperties = {
    width: '100%',
    height: '150px',
    objectFit: 'cover',
    backgroundColor: theme.colors.border,
  };

  const venueContentStyle: React.CSSProperties = {
    padding: '16px',
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  };

  const venueNameStyle: React.CSSProperties = {
    fontSize: '16px',
    fontFamily: theme.fonts.heading,
    color: theme.colors.text,
    fontWeight: 'normal',
    margin: 0,
  };

  const venueInfoStyle: React.CSSProperties = {
    fontSize: '13px',
    fontFamily: theme.fonts.body,
    color: theme.colors.textSecondary,
  };

  const venuePriceStyle: React.CSSProperties = {
    fontSize: '14px',
    fontFamily: theme.fonts.body,
    color: theme.colors.primary,
    fontWeight: '600',
  };

  const venueActionsStyle: React.CSSProperties = {
    display: 'flex',
    gap: '8px',
    marginTop: '8px',
  };

  const venueActionButtonStyle: React.CSSProperties = {
    backgroundColor: 'transparent',
    border: `1px solid ${theme.colors.border}`,
    borderRadius: '6px',
    padding: '6px 12px',
    fontSize: '12px',
    fontFamily: theme.fonts.body,
    color: theme.colors.text,
    fontWeight: '600',
    cursor: 'pointer',
    flex: 1,
  };

  const getAmenities = () => {
    const amenities = [];
    if (complex.hasParking) amenities.push('Estacionamiento');
    if (complex.hasBathrooms) amenities.push('Baños');
    if (complex.hasChangingRooms) amenities.push('Vestuarios');
    if (complex.hasBuffet) amenities.push('Buffet');
    if (complex.hasProShop) amenities.push('Pro Shop');
    complex.amenities.forEach(a => amenities.push(a));
    return amenities;
  };

  return (
    <div style={containerStyle}>
      {/* Header */}
      <div style={headerStyle}>
        <button onClick={() => navigate('/owner/dashboard')} style={backButtonStyle}>
          <ArrowLeft size={20} />
          Volver al Dashboard
        </button>

        <div style={headerContentStyle}>
          <div>
            <h1 style={titleStyle}>{complex.name}</h1>
            <p style={subtitleStyle}>{complex.address}</p>
          </div>

          <div style={actionButtonsStyle}>
            <button
              onClick={() => navigate(`/owner/complexes/${complex.id}/edit`)}
              style={editButtonStyle}
            >
              <Edit size={18} />
              Editar
            </button>

            <button
              onClick={() => navigate(`/owner/complexes/${complex.id}/add-field`)}
              style={addButtonStyle}
            >
              <Plus size={18} />
              Agregar Cancha
            </button>

            <button onClick={handleToggleActive} style={toggleButtonStyle}>
              <Power size={18} />
              {complex.isActive ? 'Desactivar' : 'Activar'}
            </button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div style={mainContentStyle}>
        {/* Sección 1: Información General */}
        <section style={sectionStyle}>
          <h2 style={sectionTitleStyle}>INFORMACIÓN GENERAL</h2>
          <div style={gridTwoColumnsStyle}>
            {/* Columna 1 - Ubicación y Contacto */}
            <div style={infoColumnStyle}>
              <div style={infoRowStyle}>
                <MapPin size={16} color={theme.colors.primary} style={{ marginTop: '2px' }} />
                <div>
                  <div style={infoLabelStyle}>Dirección</div>
                  <div style={infoValueStyle}>{complex.address}</div>
                </div>
              </div>

              <div style={infoRowStyle}>
                <MapPin size={16} color={theme.colors.textSecondary} style={{ marginTop: '2px' }} />
                <div>
                  <div style={infoLabelStyle}>Ciudad y Provincia</div>
                  <div style={infoValueStyle}>{complex.city}, {complex.province}</div>
                </div>
              </div>

              {complex.phone && (
                <div style={infoRowStyle}>
                  <Phone size={16} color={theme.colors.textSecondary} style={{ marginTop: '2px' }} />
                  <div>
                    <div style={infoLabelStyle}>Teléfono</div>
                    <div style={infoValueStyle}>{complex.phone}</div>
                  </div>
                </div>
              )}

              {complex.email && (
                <div style={infoRowStyle}>
                  <Mail size={16} color={theme.colors.textSecondary} style={{ marginTop: '2px' }} />
                  <div>
                    <div style={infoLabelStyle}>Email</div>
                    <div style={infoValueStyle}>{complex.email}</div>
                  </div>
                </div>
              )}

              {complex.website && (
                <div style={infoRowStyle}>
                  <Globe size={16} color={theme.colors.textSecondary} style={{ marginTop: '2px' }} />
                  <div>
                    <div style={infoLabelStyle}>Sitio Web</div>
                    <a href={complex.website} target="_blank" rel="noopener noreferrer" style={linkStyle}>
                      {complex.website}
                      <ExternalLink size={14} />
                    </a>
                  </div>
                </div>
              )}

              <div style={infoRowStyle}>
                <MapPin size={16} color={theme.colors.primary} style={{ marginTop: '2px' }} />
                <div>
                  <div style={infoLabelStyle}>Google Maps</div>
                  <a href={complex.mapsUrl} target="_blank" rel="noopener noreferrer" style={linkStyle}>
                    Ver en Mapa
                    <ExternalLink size={14} />
                  </a>
                </div>
              </div>
            </div>

            {/* Columna 2 - Amenities */}
            <div style={infoColumnStyle}>
              <div>
                <div style={infoLabelStyle}>Amenities del Complejo</div>
                <div style={{ marginTop: '12px' }}>
                  {getAmenities().length > 0 ? (
                    getAmenities().map((amenity, index) => (
                      <span key={index} style={amenitiesBadgeStyle}>{amenity}</span>
                    ))
                  ) : (
                    <span style={infoValueStyle}>Sin amenities</span>
                  )}
                </div>
              </div>

              {complex.description && (
                <div style={{ marginTop: '20px' }}>
                  <div style={infoLabelStyle}>Descripción</div>
                  <p style={{ ...infoValueStyle, fontWeight: 'normal', lineHeight: '1.6' }}>
                    {complex.description}
                  </p>
                </div>
              )}
            </div>
          </div>
        </section>

        {/* Sección 2: Canchas del Complejo */}
        <section style={sectionStyle}>
          <div style={sectionHeaderStyle}>
            <div>
              <h2 style={sectionTitleStyle}>CANCHAS DEL COMPLEJO</h2>
              <p style={venueCountStyle}>
                {venues.length} {venues.length === 1 ? 'cancha' : 'canchas'}
              </p>
            </div>
            <button
              onClick={() => navigate(`/owner/complexes/${complex.id}/add-field`)}
              style={addVenueButtonStyle}
            >
              <Plus size={18} />
              Agregar Cancha
            </button>
          </div>

          {venues.length === 0 ? (
            <div style={emptyStateStyle}>
              <Grid size={64} color={theme.colors.border} />
              <p style={emptyTextStyle}>Este complejo no tiene canchas todavía</p>
              <button
                onClick={() => navigate(`/owner/complexes/${complex.id}/add-field`)}
                style={addFirstVenueButtonStyle}
              >
                Agregar Primera Cancha
              </button>
            </div>
          ) : (
            <div style={venuesGridStyle}>
              {venues.map(venue => {
                const photoUrl = venue.photos?.[0]?.url || 'https://via.placeholder.com/300x150/23272A/00E676?text=Sin+Foto';
                return (
                  <div key={venue.id} style={venueCardStyle}>
                    <img src={photoUrl} alt={venue.name} style={venueImageStyle} />
                    <div style={venueContentStyle}>
                      <h3 style={venueNameStyle}>{venue.name}</h3>
                      <p style={venueInfoStyle}>
                        {(venue.venueType || 'N/A').replace('_', ' ')} • {venue.surfaceType || 'N/A'}
                      </p>
                      <p style={venuePriceStyle}>
                        ${(venue.hourlyRate || 0).toLocaleString()}/hora
                      </p>
                      <p style={venueInfoStyle}>
                        Estado: {venue.isActive ? '✅ Activa' : '❌ Inactiva'}
                      </p>

                      <div style={venueActionsStyle}>
                        <button
                          onClick={() => navigate(`/owner/venues/${venue.id}/edit`)}
                          style={venueActionButtonStyle}
                        >
                          Ver/Editar
                        </button>
                        <button
                          onClick={() => navigate(`/owner/venues/${venue.id}/availability`)}
                          style={venueActionButtonStyle}
                        >
                          <Clock size={14} /> Horarios
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </section>
      </div>
    </div>
  );
};

export default ComplexDetailPage;
